import React, { useState, useRef, useEffect } from "react";
import { formatBJ, checkTradingHours, smartInterval } from "@/lib/timeUtils";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Activity, Wifi, WifiOff, AlertTriangle, Pause, Play,
  Trash2, ChevronDown, ChevronUp, ArrowLeft, Clock,
  TrendingUp, TrendingDown, BarChart3, RefreshCw,
  CheckCircle2, XCircle, Radio, Download, RotateCcw,
  CheckCircle, Loader2, AlertCircle, FileText, ScrollText,
  Terminal, Search, Filter, Calendar, Square, Eye, Shield,
  Server, Power, PowerOff, Monitor, Globe, Zap
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "wouter";

export default function StrategyRuns() {
  const { user } = useAuth();
  const _isTH = checkTradingHours().isTradingHours;
  const { data: runs, isLoading, refetch } = trpc.strategyRun.list.useQuery(undefined, {
    enabled: !!user,
    refetchInterval: smartInterval(10000, _isTH, 60000),
  });
  const { data: stats } = trpc.strategyRun.stats.useQuery(undefined, {
    enabled: !!user,
    refetchInterval: smartInterval(10000, _isTH, 60000),
  });
  const [expandedRunId, setExpandedRunId] = useState<number | null>(null);

  const deleteMutation = trpc.strategyRun.delete.useMutation({
    onSuccess: () => {
      toast.success("已删除运行记录");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const isOnline = (lastHeartbeat: number | null) => {
    if (!lastHeartbeat) return false;
    return Date.now() - lastHeartbeat < 3 * 60 * 1000;
  };

  const formatTime = (ts: number | null) => {
    if (!ts) return "--";
    return formatBJ(ts, "datetime");
  };

  const formatDuration = (startTs: number | null) => {
    if (!startTs) return "--";
    const diff = Date.now() - startTs;
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    if (hours > 24) return `${Math.floor(hours / 24)}天${hours % 24}小时`;
    if (hours > 0) return `${hours}小时${minutes}分`;
    return `${minutes}分钟`;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link href="/"><Button variant="ghost" size="icon"><ArrowLeft className="h-5 w-5" /></Button></Link>
          <h1 className="text-2xl font-bold">策略运行状态</h1>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 顶部标题 */}
      <div className="flex items-center gap-4">
        <Link href="/">
          <Button variant="ghost" size="icon"><ArrowLeft className="h-5 w-5" /></Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Radio className="h-6 w-6 text-green-500" />
            策略运行状态
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            掘金终端导出策略的实时运行状态和交易记录回调
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />刷新
        </Button>
      </div>

      {/* 统计概览 */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><Activity className="h-3 w-3" />策略总数</p>
              <p className="text-2xl font-bold mt-1">
                <span className="text-green-400">{stats.activeCount}</span>
                <span className="text-muted-foreground text-sm"> / {stats.total}</span>
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">运行中 / 总数</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><BarChart3 className="h-3 w-3" />总交易笔数</p>
              <p className="text-2xl font-bold mt-1">{stats.totalTrades}</p>
              <p className="text-xs text-muted-foreground mt-0.5">累计执行交易</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                {stats.totalPnl >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                累计盈亏
              </p>
              <p className={`text-2xl font-bold mt-1 ${stats.totalPnl >= 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                {stats.totalPnl >= 0 ? '+' : ''}{stats.totalPnl.toFixed(2)}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">元</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1"><Wifi className="h-3 w-3" />在线策略</p>
              <p className="text-2xl font-bold mt-1 text-green-400">
                {runs?.filter(r => r.status === "running" && isOnline(r.lastHeartbeat)).length || 0}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">3分钟内有心跳</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* 多引擎冲突检测 */}
      <EngineInstancesPanel />

      {/* 引擎更新状态 */}
      <UpdateProgressPanel />

      {/* 更新日志 */}
      <ChangelogPanel />

      {/* 看门狗配置 */}
      <WatchdogConfigPanel />

      {/* 引擎日志 */}
      <EngineLogsPanel />

      {/* 看门狗日志 */}
      <WatchdogLogsPanel />
      {/* 买卖配对检查 */}
      <TradePairCheckPanel />
      {/* 行情延迟统计 */}
      <QuoteLatencyPanel />

      {/* 策略列表 */}
      {(!runs || runs.length === 0) ? (
        <Card className="bg-card border-border/50">
          <CardContent className="py-12 text-center">
            <Radio className="h-12 w-12 mx-auto mb-3 opacity-30 text-muted-foreground" />
            <p className="text-muted-foreground">暂无策略运行记录</p>
            <p className="text-xs text-muted-foreground mt-1">
              在掘金策略导出页面启用"交易回调"后，运行策略即可在此查看状态
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {runs.map((run: any) => {
            const online = isOnline(run.lastHeartbeat);
            const isExpanded = expandedRunId === run.id;
            const winRate = run.totalTrades > 0 ? ((run.successTrades || 0) / run.totalTrades * 100) : 0;

            return (
              <Card key={run.id} className="bg-card border-border/50">
                <CardContent className="p-4">
                  {/* 主行 */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {run.status === "running" ? (
                        online ? <Wifi className="h-4 w-4 text-green-400" /> : <WifiOff className="h-4 w-4 text-yellow-400" />
                      ) : run.status === "error" ? (
                        <XCircle className="h-4 w-4 text-red-400" />
                      ) : run.status === "completed" ? (
                        <CheckCircle2 className="h-4 w-4 text-blue-400" />
                      ) : (
                        <Pause className="h-4 w-4 text-muted-foreground" />
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{run.stockName || run.stockCode}</span>
                          <span className="text-xs font-mono text-muted-foreground">{run.stockCode}</span>
                          <Badge variant={run.status === "running" ? (online ? "default" : "secondary") : run.status === "error" ? "destructive" : "secondary"} className="text-[10px]">
                            {run.status === "running" ? (online ? "在线运行" : "离线") : run.status === "error" ? "异常" : run.status === "completed" ? "已完成" : "已停止"}
                          </Badge>
                          <Badge variant="outline" className="text-[10px]">
                            {run.mode === "live" ? "仿真/实盘" : "回测"}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />启动: {formatTime(run.startedAt)}</span>
                          <span>运行: {formatDuration(run.startedAt)}</span>
                          {run.lastHeartbeat && <span>心跳: {formatTime(run.lastHeartbeat)}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {/* 统计摘要 */}
                      <div className="text-right hidden sm:block">
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-muted-foreground">{run.totalTrades}笔</span>
                          <span className={`font-medium ${run.totalPnl >= 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                            {run.totalPnl >= 0 ? '+' : ''}{run.totalPnl?.toFixed(2) || '0.00'}元
                          </span>
                          {run.totalTrades > 0 && (
                            <span className={`text-xs ${winRate >= 50 ? 'text-red-400' : 'text-emerald-400'}`}>
                              胜率{winRate.toFixed(0)}%
                            </span>
                          )}
                        </div>
                        {run.currentPosition > 0 && (
                          <div className="text-xs text-muted-foreground mt-0.5">
                            持仓: {run.currentPosition}股 @ {run.avgCost?.toFixed(2) || '--'}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost" size="icon" className="h-8 w-8"
                          onClick={() => setExpandedRunId(isExpanded ? null : run.id)}
                        >
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                          onClick={() => {
                            if (confirm("确定删除此运行记录？")) deleteMutation.mutate({ id: run.id });
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* 展开详情 */}
                  {isExpanded && (
                    <ExpandedRunDetail runId={run.id} run={run} />
                  )}

                  {/* 错误信息 */}
                  {run.lastError && (
                    <div className="mt-2 p-2 rounded bg-red-500/10 border border-red-500/20 text-xs text-red-400">
                      <AlertTriangle className="h-3 w-3 inline mr-1" />
                      {run.lastError}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// 引擎控制按钮组（停止/启动/重启）
function EngineControlButtons({ connected, enginePaused }: { connected: boolean; enginePaused: boolean }) {
  const [confirmAction, setConfirmAction] = useState<'stop' | 'restart' | 'start' | null>(null);
  const [commandSent, setCommandSent] = useState<'stop' | 'restart' | 'start' | null>(null);
  const [showUnfilledWarning, setShowUnfilledWarning] = useState(false);
  const [unfilledData, setUnfilledData] = useState<{ count: number; stocks: Array<{ id: number; stockCode: string; stockName: string; tradeLotSize: number | null }> } | null>(null);
  const [checkingUnfilled, setCheckingUnfilled] = useState(false);
  const utils = trpc.useUtils();

  // 查询待执行命令状态
  const { data: pendingCmd } = trpc.engineUpdate.pendingCommand.useQuery(undefined, {
    refetchInterval: commandSent ? 2000 : 5000, // 命令下发后缩短轮询间隔
  });

  // 当命令被消费后清除commandSent状态
  useEffect(() => {
    if (commandSent && !pendingCmd?.hasPending) {
      // 命令已被引擎消费，刷新心跳状态
      utils.tradingProxy.heartbeatStatus.invalidate();
      const timer = setTimeout(() => {
        setCommandSent(null);
      }, 3000); // 3秒后清除过渡状态
      return () => clearTimeout(timer);
    }
  }, [commandSent, pendingCmd?.hasPending]);

  const stopMut = trpc.engineUpdate.stopEngine.useMutation({
    onSuccess: (data) => {
      toast.success(data.message || '停止命令已下发');
      setConfirmAction(null);
      setCommandSent('stop');
      // 立即刷新心跳状态
      utils.tradingProxy.heartbeatStatus.invalidate();
      utils.engineUpdate.pendingCommand.invalidate();
    },
    onError: (err) => { toast.error(`下发失败: ${err.message}`); setConfirmAction(null); },
  });
  const restartMut = trpc.engineUpdate.restartEngine.useMutation({
    onSuccess: (data) => {
      toast.success(data.message || '重启命令已下发');
      setConfirmAction(null);
      setCommandSent('restart');
      utils.tradingProxy.heartbeatStatus.invalidate();
      utils.engineUpdate.pendingCommand.invalidate();
    },
    onError: (err) => { toast.error(`下发失败: ${err.message}`); setConfirmAction(null); },
  });
  const startMut = trpc.engineUpdate.startEngine.useMutation({
    onSuccess: (data) => {
      toast.success(data.message || '启动命令已下发');
      setConfirmAction(null);
      setCommandSent('start');
      utils.tradingProxy.heartbeatStatus.invalidate();
      utils.engineUpdate.pendingCommand.invalidate();
    },
    onError: (err) => { toast.error(`下发失败: ${err.message}`); setConfirmAction(null); },
  });

  const isPending = stopMut.isPending || restartMut.isPending || startMut.isPending;

  // 命令已下发，等待引擎响应的过渡状态
  if (commandSent && pendingCmd?.hasPending) {
    const cmdLabels: Record<string, string> = {
      stop: '停止命令已下发，等待引擎响应...',
      restart: '重启命令已下发，等待引擎响应...',
      start: '启动命令已下发，等待看门狗响应...',
    };
    return (
      <div className="flex items-center gap-1">
        <Loader2 className="h-3 w-3 animate-spin text-amber-400" />
        <span className="text-[10px] text-amber-400">{cmdLabels[commandSent]}</span>
      </div>
    );
  }

  // 命令已被消费，等待状态更新
  if (commandSent && !pendingCmd?.hasPending) {
    const doneLabels: Record<string, string> = {
      stop: '引擎正在停止...',
      restart: '引擎正在重启...',
      start: '引擎正在启动...',
    };
    return (
      <div className="flex items-center gap-1">
        <Loader2 className="h-3 w-3 animate-spin text-blue-400" />
        <span className="text-[10px] text-blue-400">{doneLabels[commandSent]}</span>
      </div>
    );
  }

  // 紧急停止前检查未做平持仓
  const handleStopWithCheck = async () => {
    setCheckingUnfilled(true);
    try {
      const result = await utils.client.engineUpdate.checkUnfilledPositions.query();
      if (result.hasUnfilled) {
        setUnfilledData(result);
        setShowUnfilledWarning(true);
      } else {
        setConfirmAction('stop');
      }
    } catch (err) {
      // 查询失败时直接显示确认
      setConfirmAction('stop');
    } finally {
      setCheckingUnfilled(false);
    }
  };

  // 未做平警告弹窗
  if (showUnfilledWarning && unfilledData) {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 text-amber-400" />
          <span className="text-[10px] text-amber-400 font-medium">
            {unfilledData.count} 只股票未做平，确认停止?
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-5 text-[10px] px-1.5 text-red-400"
            onClick={() => { stopMut.mutate(); setShowUnfilledWarning(false); setUnfilledData(null); }} disabled={isPending}>
            {isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : '确认停止'}
          </Button>
          <Button variant="ghost" size="sm" className="h-5 text-[10px] px-1.5" onClick={() => { setShowUnfilledWarning(false); setUnfilledData(null); }}>取消</Button>
        </div>
      </div>
    );
  }

  // 确认弹窗
  if (confirmAction) {
    const actionConfig = {
      stop: { label: '确认停止? 引擎将进入待命模式', btnText: '确认停止', color: 'text-red-400', fn: () => stopMut.mutate() },
      restart: { label: '确认重启?', btnText: '确认重启', color: 'text-amber-400', fn: () => restartMut.mutate() },
      start: { label: '确认启动引擎?', btnText: '确认启动', color: 'text-green-400', fn: () => startMut.mutate() },
    }[confirmAction];
    return (
      <div className="flex items-center gap-1">
        <span className={`text-[10px] ${actionConfig.color}`}>{actionConfig.label}</span>
        <Button variant="ghost" size="sm" className={`h-5 text-[10px] px-1.5 ${actionConfig.color}`}
          onClick={actionConfig.fn} disabled={isPending}>
          {isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : actionConfig.btnText}
        </Button>
        <Button variant="ghost" size="sm" className="h-5 text-[10px] px-1.5" onClick={() => setConfirmAction(null)}>取消</Button>
      </div>
    );
  }

  // 引擎已停止（待命模式）—— 显示启动按钮
  if (enginePaused || !connected) {
    return (
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm"
          className="h-6 text-xs text-green-400 hover:text-green-300 hover:bg-green-500/10"
          onClick={() => setConfirmAction('start')}>
          <Play className="h-3 w-3 mr-1" />启动引擎
        </Button>
      </div>
    );
  }

  // 引擎运行中 —— 显示停止和重启按钮
  return (
    <div className="flex items-center gap-1">
      <Button variant="ghost" size="sm"
        className="h-6 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
        onClick={handleStopWithCheck} disabled={checkingUnfilled}>
        {checkingUnfilled ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Square className="h-3 w-3 mr-1" />}紧急停止
      </Button>
      <Button variant="ghost" size="sm"
        className="h-6 text-xs text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
        onClick={() => setConfirmAction('restart')}>
        <RefreshCw className="h-3 w-3 mr-1" />远程重启
      </Button>
    </div>
  );
}

function UpdateProgressPanel() {
  const { user } = useAuth();
  const { data: progress } = trpc.engineUpdate.updateProgress.useQuery(undefined, {
    enabled: !!user,
    refetchInterval: 5000,
  });
  const { data: hbStatus } = trpc.tradingProxy.heartbeatStatus.useQuery(undefined, {
    enabled: !!user,
    refetchInterval: 5000, // 5秒轮询，加快状态响应
  });
  const { data: versionInfo } = trpc.engineUpdate.currentWebVersion.useQuery(undefined, {
    enabled: !!user,
  });
  const { data: pendingCmd } = trpc.engineUpdate.pendingCommand.useQuery(undefined, {
    enabled: !!user,
    refetchInterval: 5000,
  });
  const utils = trpc.useUtils();
  const clearMut = trpc.engineUpdate.clearProgress.useMutation({
    onSuccess: () => {
      utils.engineUpdate.updateProgress.invalidate();
    },
  });

  const phaseLabels: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
    idle: { label: "空闲", icon: <CheckCircle className="h-4 w-4" />, color: "text-muted-foreground" },
    checking: { label: "检查更新", icon: <Loader2 className="h-4 w-4 animate-spin" />, color: "text-blue-400" },
    downloading: { label: "下载中", icon: <Download className="h-4 w-4 animate-bounce" />, color: "text-blue-400" },
    verifying: { label: "校验中", icon: <Loader2 className="h-4 w-4 animate-spin" />, color: "text-yellow-400" },
    stopping: { label: "停止引擎", icon: <AlertCircle className="h-4 w-4" />, color: "text-orange-400" },
    extracting: { label: "解压中", icon: <FileText className="h-4 w-4" />, color: "text-purple-400" },
    migrating: { label: "数据库迁移", icon: <RotateCcw className="h-4 w-4 animate-spin" />, color: "text-purple-400" },
    restarting: { label: "重启中", icon: <RefreshCw className="h-4 w-4 animate-spin" />, color: "text-green-400" },
    completed: { label: "更新完成", icon: <CheckCircle className="h-4 w-4" />, color: "text-green-400" },
    failed: { label: "更新失败", icon: <XCircle className="h-4 w-4" />, color: "text-red-400" },
    rolling_back: { label: "回滚中", icon: <RotateCcw className="h-4 w-4 animate-spin" />, color: "text-orange-400" },
  };

  const isUpdating = progress && progress.phase !== 'idle' && progress.active;
  const hasRecentResult = progress && progress.phase !== 'idle' && !progress.active;
  const phaseInfo = progress ? (phaseLabels[progress.phase] || { label: progress.phase, icon: <Loader2 className="h-4 w-4" />, color: "text-muted-foreground" }) : phaseLabels.idle;

  return (
    <Card className={`border-border/50 ${
      isUpdating ? 'bg-blue-500/5 border-blue-500/30' 
      : hasRecentResult && progress?.phase === 'failed' ? 'bg-red-500/5 border-red-500/30' 
      : hasRecentResult && progress?.phase === 'completed' ? 'bg-green-500/5 border-green-500/30' 
      : 'bg-card'
    }`}>
      <CardContent className="p-4">
        {/* 标题行 */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ScrollText className="h-4 w-4 text-blue-400" />
            <span className="text-sm font-medium">引擎更新状态</span>
          </div>
          <div className="flex items-center gap-1">
            <EngineControlButtons connected={!!hbStatus?.connected} enginePaused={hbStatus?.heartbeatStatus === 'watchdog_standby' || hbStatus?.t0EngineStatus?.status === 'watchdog_standby'} />
            <Link href="/engine-versions">
              <Button variant="ghost" size="sm" className="h-6 text-xs">
                版本管理 →
              </Button>
            </Link>
          </div>
        </div>

        {/* 引擎连接状态 + 版本信息 */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
          <div className="p-2 rounded-lg bg-muted/30 text-center">
            <div className="text-[10px] text-muted-foreground mb-0.5">引擎连接</div>
            <div className={`text-sm font-medium flex items-center justify-center gap-1 ${
              hbStatus?.connected ? 'text-green-400' : 'text-red-400'
            }`}>
              {hbStatus?.connected ? (
                <><Wifi className="h-3 w-3" />在线</>
              ) : (
                <><WifiOff className="h-3 w-3" />离线</>
              )}
            </div>
          </div>
          <div className="p-2 rounded-lg bg-muted/30 text-center">
            <div className="text-[10px] text-muted-foreground mb-0.5">引擎端版本</div>
            <div className={`text-sm font-medium font-mono ${
              hbStatus?.connected && hbStatus?.t0EngineStatus?.version
                ? hbStatus.t0EngineStatus.version === versionInfo?.latestPublishedVersion
                  ? 'text-cyan-400'
                  : 'text-amber-400'
                : 'text-muted-foreground'
            }`}>
              {hbStatus?.connected && hbStatus?.t0EngineStatus?.version
                ? hbStatus.t0EngineStatus.version
                : '--'}
            </div>
          </div>
          <div className="p-2 rounded-lg bg-muted/30 text-center">
            <div className="text-[10px] text-muted-foreground mb-0.5">Web端版本</div>
            <div className="text-sm font-medium font-mono">
              {versionInfo?.fileVersion || '--'}
            </div>
          </div>
          <div className="p-2 rounded-lg bg-muted/30 text-center">
            <div className="text-[10px] text-muted-foreground mb-0.5">已发布版本</div>
            <div className="text-sm font-medium font-mono">
              {versionInfo?.latestPublishedVersion || '--'}
            </div>
          </div>
          <div className="p-2 rounded-lg bg-muted/30 text-center">
            <div className="text-[10px] text-muted-foreground mb-0.5">更新状态</div>
            <div className={`text-sm font-medium flex items-center justify-center gap-1 ${phaseInfo.color}`}>
              {isUpdating ? (
                <><Loader2 className="h-3 w-3 animate-spin" />{phaseInfo.label}</>
              ) : hasRecentResult ? (
                <>{phaseInfo.icon}<span className="text-xs">{phaseInfo.label}</span></>
              ) : (
                <><CheckCircle className="h-3 w-3" />空闲</>
              )}
            </div>
          </div>
        </div>

        {/* 待下发命令提示 */}
        {pendingCmd?.hasPending && (
          <div className="mb-2 p-2 rounded bg-yellow-500/10 border border-yellow-500/20 text-xs text-yellow-400 flex items-center gap-2">
            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
            <span>待下发{pendingCmd.command === 'update' ? '更新' : '回滚'}命令：v{pendingCmd.version}（等待引擎下次心跳接收）</span>
          </div>
        )}

        {/* 更新进度条（正在更新时显示） */}
        {isUpdating && progress?.percent !== undefined && (
          <div className="mb-2">
            <Progress value={progress.percent} className="h-1.5 mb-1" />
            <div className="flex items-center gap-2">
              <span className={phaseInfo.color}>{phaseInfo.icon}</span>
              <span className="text-xs text-muted-foreground">{progress.message || phaseInfo.label}</span>
              {progress.targetVersion && (
                <span className="text-xs text-muted-foreground">→ v{progress.targetVersion}</span>
              )}
              {progress.updatedAt && (
                <span className="text-xs text-muted-foreground ml-auto">
                  {new Date(progress.updatedAt).toLocaleTimeString('zh-CN')}
                </span>
              )}
            </div>
          </div>
        )}

        {/* 最近更新结果（已完成/失败时显示） */}
        {hasRecentResult && (
          <div className={`p-2 rounded text-xs flex items-center justify-between ${
            progress?.phase === 'completed' ? 'bg-green-500/10 border border-green-500/20 text-green-400'
            : progress?.phase === 'failed' ? 'bg-red-500/10 border border-red-500/20 text-red-400'
            : 'bg-muted/30 text-muted-foreground'
          }`}>
            <div className="flex items-center gap-2">
              {phaseInfo.icon}
              <span>{progress?.message || phaseInfo.label}</span>
              {progress?.targetVersion && <span className="font-mono">v{progress.targetVersion}</span>}
            </div>
            <div className="flex items-center gap-2">
              {progress?.updatedAt && (
                <span className="text-muted-foreground">
                  {new Date(progress.updatedAt).toLocaleTimeString('zh-CN')}
                </span>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                className="h-6 text-xs px-2 border-current/30 hover:bg-current/10" 
                onClick={() => clearMut.mutate()}
                disabled={clearMut.isPending}
              >
                {clearMut.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                清除状态
              </Button>
            </div>
          </div>
        )}

        {/* 心跳详情 */}
        {hbStatus?.connected && hbStatus.lastHeartbeat && (
          <div className="mt-2 text-[10px] text-muted-foreground/60 flex items-center gap-3">
            <span>最近心跳: {new Date(hbStatus.lastHeartbeat).toLocaleTimeString('zh-CN')}</span>
            <span>延迟: {Math.round((hbStatus.elapsedMs || 0) / 1000)}秒前</span>
            {hbStatus.heartbeatStatus && <span>状态: {hbStatus.heartbeatStatus}</span>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// 展开的运行详情（包含交易记录）
function ExpandedRunDetail({ runId, run }: { runId: number; run: any }) {
  const { data: trades, isLoading } = trpc.strategyRun.trades.useQuery({ runId });

  return (
    <div className="mt-4 pt-4 border-t border-border/50">
      {/* 详细统计 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="p-2 rounded-lg bg-muted/30 text-center">
          <div className="text-sm font-bold">{run.totalTrades}</div>
          <div className="text-[10px] text-muted-foreground">总交易</div>
        </div>
        <div className="p-2 rounded-lg bg-muted/30 text-center">
          <div className="text-sm font-bold">{run.successTrades || 0}</div>
          <div className="text-[10px] text-muted-foreground">盈利笔数</div>
        </div>
        <div className="p-2 rounded-lg bg-muted/30 text-center">
          <div className={`text-sm font-bold ${(run.totalPnl || 0) >= 0 ? 'text-red-400' : 'text-emerald-400'}`}>
            {(run.totalPnl || 0) >= 0 ? '+' : ''}{(run.totalPnl || 0).toFixed(2)}
          </div>
          <div className="text-[10px] text-muted-foreground">净盈亏</div>
        </div>
        <div className="p-2 rounded-lg bg-muted/30 text-center">
          <div className="text-sm font-bold text-muted-foreground">{(run.totalFees || 0).toFixed(2)}</div>
          <div className="text-[10px] text-muted-foreground">总手续费</div>
        </div>
      </div>

      {/* 交易记录 */}
      <div>
        <h4 className="text-sm font-medium mb-2">交易记录</h4>
        {isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : !trades || trades.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-4">暂无交易记录</p>
        ) : (
          <div className="space-y-1 max-h-[300px] overflow-auto">
            {trades.map((trade: any) => (
              <div key={trade.id} className="flex items-center justify-between text-xs px-2 py-1.5 rounded bg-muted/20">
                <div className="flex items-center gap-2">
                  {trade.tradeType === "buy" ? (
                    <Badge className="text-[9px] bg-red-500/20 text-red-400 border-red-500/30">买入</Badge>
                  ) : (
                    <Badge className="text-[9px] bg-emerald-500/20 text-emerald-400 border-emerald-500/30">卖出</Badge>
                  )}
                  <span>{trade.volume}股</span>
                  <span className="text-muted-foreground">@ {trade.price?.toFixed(2)}</span>
                  <span className="text-muted-foreground">金额: {trade.amount?.toFixed(0)}</span>
                  {trade.scenario && <Badge variant="outline" className="text-[9px]">{trade.scenario}</Badge>}
                </div>
                <div className="flex items-center gap-3">
                  {trade.pnl !== 0 && trade.pnl !== null && (
                    <span className={trade.pnl >= 0 ? 'text-red-400' : 'text-emerald-400'}>
                      {trade.pnl >= 0 ? '+' : ''}{trade.pnl?.toFixed(2)}
                    </span>
                  )}
                  <span className="text-muted-foreground">
                    {trade.tradeTime ? formatBJ(trade.tradeTime, "time") : '--'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// 版本更新日志面板
function WatchdogConfigPanel() {
  const { user } = useAuth();
  const { data: config, isLoading } = trpc.engineUpdate.getWatchdogConfig.useQuery(undefined, {
    enabled: !!user,
  });
  const saveMut = trpc.engineUpdate.saveWatchdogConfig.useMutation({
    onSuccess: () => toast.success("看门狗配置已保存"),
    onError: (err) => toast.error(err.message),
  });
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState<any>(null);

  useEffect(() => {
    if (config && !form) {
      setForm({ ...config });
    }
  }, [config]);

  const handleSave = () => {
    if (!form) return;
    saveMut.mutate(form);
  };

  if (isLoading || !config) {
    return <Skeleton className="h-20 w-full" />;
  }

  return (
    <Card className="bg-card border-border/50">
      <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <CardTitle className="text-sm font-medium flex items-center justify-between">
          <span className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-orange-400" />
            看门狗 / NSSM 恢复策略
            {config.nssmEnabled && <Badge variant="secondary" className="text-[10px]">NSSM 已启用</Badge>}
          </span>
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </CardTitle>
        <CardDescription className="text-xs">
          配置引擎崩溃后的自动重启策略，下次引擎心跳时同步生效
        </CardDescription>
      </CardHeader>
      {expanded && form && (
        <CardContent className="p-4 pt-0 space-y-4">
          {/* 看门狗基础配置 */}
          <div>
            <h4 className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
              <Activity className="h-3 w-3" />看门狗基础配置
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="text-[10px] text-muted-foreground">最大重启次数</label>
                <Input
                  type="number" className="h-8 text-xs mt-1"
                  value={form.maxRestarts}
                  onChange={(e) => setForm({ ...form, maxRestarts: parseInt(e.target.value) || 10 })}
                />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">基础重启延迟(秒)</label>
                <Input
                  type="number" className="h-8 text-xs mt-1"
                  value={form.restartDelay}
                  onChange={(e) => setForm({ ...form, restartDelay: parseFloat(e.target.value) || 3 })}
                />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">崩溃窗口(秒)</label>
                <Input
                  type="number" className="h-8 text-xs mt-1"
                  value={form.crashWindow}
                  onChange={(e) => setForm({ ...form, crashWindow: parseInt(e.target.value) || 600 })}
                />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">最大重启延迟(秒)</label>
                <Input
                  type="number" className="h-8 text-xs mt-1"
                  value={form.maxRestartDelay}
                  onChange={(e) => setForm({ ...form, maxRestartDelay: parseInt(e.target.value) || 60 })}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* NSSM 恢复策略 */}
          <div>
            <h4 className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
              <RefreshCw className="h-3 w-3" />NSSM 服务恢复策略
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] text-muted-foreground">重启延迟(ms)</label>
                <Input
                  type="number" className="h-8 text-xs mt-1"
                  value={form.nssmAppRestartDelay}
                  onChange={(e) => setForm({ ...form, nssmAppRestartDelay: parseInt(e.target.value) || 10000 })}
                />
                <p className="text-[9px] text-muted-foreground mt-0.5">崩溃后等待多久重启</p>
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">节流间隔(ms)</label>
                <Input
                  type="number" className="h-8 text-xs mt-1"
                  value={form.nssmAppThrottle}
                  onChange={(e) => setForm({ ...form, nssmAppThrottle: parseInt(e.target.value) || 30000 })}
                />
                <p className="text-[9px] text-muted-foreground mt-0.5">快速崩溃时的最小间隔</p>
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">停止超时(ms)</label>
                <Input
                  type="number" className="h-8 text-xs mt-1"
                  value={form.nssmAppStopMethodConsole}
                  onChange={(e) => {
                    const v = parseInt(e.target.value) || 3000;
                    setForm({ ...form, nssmAppStopMethodConsole: v, nssmAppStopMethodWindow: v, nssmAppStopMethodThreads: v });
                  }}
                />
                <p className="text-[9px] text-muted-foreground mt-0.5">优雅停止等待时间</p>
              </div>
            </div>
            <div className="mt-2 p-2 rounded bg-muted/30 text-[10px] text-muted-foreground">
              <p>提示：使用 <code className="bg-muted px-1 rounded">python watchdog.py --install-nssm</code> 一键安装 NSSM 服务（包含自动恢复策略）</p>
            </div>
          </div>

          <div className="flex justify-end">
            <Button size="sm" onClick={handleSave} disabled={saveMut.isPending}>
              {saveMut.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              保存配置
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function ChangelogPanel() {
  const { user } = useAuth();
  const { data: versionData, isLoading } = trpc.engineUpdate.listVersions.useQuery(undefined, {
    enabled: !!user,
  });
  const [showAll, setShowAll] = useState(false);

  if (isLoading) {
    return <Skeleton className="h-32 w-full" />;
  }

  const versions = versionData?.versions || [];
  if (versions.length === 0) {
    return (
      <Card className="bg-card border-border/50">
        <CardContent className="py-8 text-center">
          <ScrollText className="h-8 w-8 mx-auto mb-2 opacity-30 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">暂无版本更新日志</p>
          <p className="text-xs text-muted-foreground mt-1">
            在版本管理页面发布新版本后，更新日志将在此显示
          </p>
        </CardContent>
      </Card>
    );
  }

  // 按发布时间倒序排列
  const sortedVersions = [...versions].sort((a: any, b: any) => {
    const ta = new Date(a.publishedAt || 0).getTime();
    const tb = new Date(b.publishedAt || 0).getTime();
    return tb - ta;
  });

  const displayVersions = showAll ? sortedVersions : sortedVersions.slice(0, 3);

  return (
    <Card className="bg-card border-border/50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center justify-between">
          <span className="flex items-center gap-2">
            <ScrollText className="h-4 w-4 text-blue-400" />
            版本更新日志
            <Badge variant="secondary" className="text-[10px]">{versions.length} 个版本</Badge>
          </span>
          {versions.length > 3 && (
            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setShowAll(!showAll)}>
              {showAll ? '收起' : `查看全部 (${versions.length})`}
              {showAll ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div className="space-y-3">
          {displayVersions.map((v: any, idx: number) => (
            <div key={v.version} className={`relative pl-6 ${idx < displayVersions.length - 1 ? 'pb-3 border-b border-border/30' : ''}`}>
              {/* 时间线圆点 */}
              <div className={`absolute left-0 top-1 w-3 h-3 rounded-full border-2 ${
                idx === 0 ? 'bg-blue-500 border-blue-400' : 'bg-muted border-border'
              }`} />
              {idx < displayVersions.length - 1 && (
                <div className="absolute left-[5px] top-4 w-0.5 h-full bg-border/50" />
              )}
              
              <div className="flex items-center gap-2 mb-1">
                <Badge variant={idx === 0 ? "default" : "outline"} className="text-[10px] font-mono">
                  v{v.version}
                </Badge>
                {idx === 0 && <Badge variant="secondary" className="text-[9px]">最新</Badge>}
                {v.publishedAt && (
                  <span className="text-[10px] text-muted-foreground">
                    {new Date(v.publishedAt).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
                {v.packageSize > 0 && (
                  <span className="text-[10px] text-muted-foreground">
                    {(v.packageSize / 1024 / 1024).toFixed(1)} MB
                  </span>
                )}
              </div>
              
              {v.changelog ? (
                <div className="text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed">
                  {v.changelog}
                </div>
              ) : (
                <div className="text-xs text-muted-foreground/50 italic">无更新说明</div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// 引擎端远程日志查看面板
function EngineLogsPanel() {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [maxLines, setMaxLines] = useState(200);
  const [isExpanded, setIsExpanded] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const logContainerRef = useRef<HTMLDivElement>(null);

  const { data: logsData, isLoading, refetch, isFetching } = trpc.tradingProxy.engineLogs.useQuery(
    { lines: maxLines, search: searchTerm, level: levelFilter, date: dateFilter },
    {
      enabled: !!user && isExpanded,
      refetchInterval: autoRefresh ? 5000 : false,
      staleTime: 3000,
    }
  );

  // 自动滚动到底部
  useEffect(() => {
    if (logContainerRef.current && autoRefresh) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logsData?.lines, autoRefresh]);

  const getLogLevelColor = (line: string) => {
    if (line.includes('| ERROR') || line.includes('ERROR')) return 'text-red-400';
    if (line.includes('| WARNING') || line.includes('WARNING')) return 'text-yellow-400';
    if (line.includes('| DEBUG') || line.includes('DEBUG')) return 'text-gray-500';
    if (line.includes('| INFO') || line.includes('INFO')) return 'text-blue-300';
    return 'text-gray-300';
  };

  const getLogLevelBg = (line: string) => {
    if (line.includes('| ERROR') || line.includes('ERROR')) return 'bg-red-500/5';
    if (line.includes('| WARNING') || line.includes('WARNING')) return 'bg-yellow-500/5';
    return '';
  };

  return (
    <Card className="bg-card border-border/50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center justify-between">
          <span
            className="flex items-center gap-2 cursor-pointer select-none"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Terminal className="h-4 w-4 text-emerald-400" />
            引擎远程日志
            {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </span>
          {isExpanded && (
            <div className="flex items-center gap-2">
              <Button
                variant={autoRefresh ? "default" : "outline"}
                size="sm"
                className="h-6 text-[10px] gap-1"
                onClick={() => setAutoRefresh(!autoRefresh)}
              >
                {autoRefresh ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                {autoRefresh ? '自动刷新中' : '自动刷新'}
              </Button>
              <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1" onClick={() => refetch()}>
                <RefreshCw className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`} />
                刷新
              </Button>
            </div>
          )}
        </CardTitle>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0">
          {/* 过滤工具栏 */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="relative flex-1 min-w-[150px]">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="搜索日志内容..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-7 text-xs pl-7"
              />
            </div>
            <Select value={levelFilter || 'all'} onValueChange={(v) => setLevelFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-7 w-[100px] text-xs">
                <Filter className="h-3 w-3 mr-1" />
                <SelectValue placeholder="级别" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部</SelectItem>
                <SelectItem value="DEBUG">DEBUG</SelectItem>
                <SelectItem value="INFO">INFO</SelectItem>
                <SelectItem value="WARNING">WARNING</SelectItem>
                <SelectItem value="ERROR">ERROR</SelectItem>
              </SelectContent>
            </Select>
            {logsData?.available_files && logsData.available_files.length > 0 && (
              <Select value={dateFilter || 'today'} onValueChange={(v) => setDateFilter(v === 'today' ? '' : v)}>
                <SelectTrigger className="h-7 w-[130px] text-xs">
                  <Calendar className="h-3 w-3 mr-1" />
                  <SelectValue placeholder="日期" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">今天</SelectItem>
                  {logsData.available_files.map((f: any) => (
                    <SelectItem key={f.date} value={f.date}>
                      {f.date} ({(f.size / 1024).toFixed(0)}KB)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={String(maxLines)} onValueChange={(v) => setMaxLines(Number(v))}>
              <SelectTrigger className="h-7 w-[90px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="100">100行</SelectItem>
                <SelectItem value="200">200行</SelectItem>
                <SelectItem value="500">500行</SelectItem>
                <SelectItem value="1000">1000行</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* 日志内容 */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">加载日志中...</span>
            </div>
          ) : !logsData?.connected ? (
            <div className="text-center py-8">
              <WifiOff className="h-8 w-8 mx-auto mb-2 opacity-30 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{logsData?.error || '无法连接引擎端'}</p>
              <p className="text-xs text-muted-foreground mt-1">
{logsData?.error?.includes('不支持') 
                   ? '请在版本管理页发布并推送新版本到引擎端' 
                   : '请确认引擎端已启动且网络可达'}
              </p>
            </div>
          ) : logsData.lines?.length === 0 ? (
            <div className="text-center py-8">
              <Terminal className="h-8 w-8 mx-auto mb-2 opacity-30 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {searchTerm || levelFilter ? '没有匹配的日志记录' : '日志为空'}
              </p>
            </div>
          ) : (
            <>
              {/* 统计信息 */}
              <div className="flex items-center gap-3 mb-2 text-[10px] text-muted-foreground">
                <span>文件: {logsData.file}</span>
                <span>显示: {logsData.lines.length} / {logsData.total}行</span>
                {logsData.totalAll && <span>总计: {logsData.totalAll}行</span>}
                {isFetching && <Loader2 className="h-3 w-3 animate-spin" />}
              </div>

              {/* 日志终端 */}
              <div
                ref={logContainerRef}
                className="bg-black/80 rounded-lg border border-border/30 p-3 font-mono text-[11px] leading-[1.6] max-h-[500px] overflow-auto scroll-smooth"
                style={{ tabSize: 4 }}
              >
                {logsData.lines.map((line: string, idx: number) => (
                  <div
                    key={idx}
                    className={`${getLogLevelColor(line)} ${getLogLevelBg(line)} hover:bg-white/5 px-1 rounded-sm whitespace-pre-wrap break-all`}
                  >
                    {line}
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}


function WatchdogLogsPanel() {
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [hoursFilter, setHoursFilter] = useState(24);
  const [maxLines, setMaxLines] = useState(200);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const cleanMutation = trpc.tradingProxy.cleanWatchdogLogs.useMutation({
    onSuccess: (data) => {
      toast.success(data.message);
      refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  const { data: logsData, isLoading, refetch, isFetching } = trpc.tradingProxy.watchdogLogs.useQuery(
    { limit: maxLines, level: levelFilter || undefined, source: sourceFilter || undefined, search: searchTerm || undefined, hours: hoursFilter },
    {
      enabled: !!user && isExpanded,
      refetchInterval: autoRefresh ? 5000 : false,
      staleTime: 3000,
    }
  );

  // 自动滚动到底部
  useEffect(() => {
    if (logContainerRef.current && autoRefresh) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logsData?.logs, autoRefresh]);

  const getLevelColor = (level: string) => {
    switch (level.toUpperCase()) {
      case 'ERROR': case 'CRITICAL': return 'text-red-400';
      case 'WARNING': return 'text-yellow-400';
      case 'DEBUG': return 'text-gray-500';
      case 'INFO': return 'text-blue-300';
      default: return 'text-gray-300';
    }
  };

  const getLevelBg = (level: string) => {
    switch (level.toUpperCase()) {
      case 'ERROR': case 'CRITICAL': return 'bg-red-500/5';
      case 'WARNING': return 'bg-yellow-500/5';
      default: return '';
    }
  };

  const getLevelBadge = (level: string) => {
    switch (level.toUpperCase()) {
      case 'ERROR': case 'CRITICAL': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'WARNING': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'INFO': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'DEBUG': return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getSourceBadge = (source: string) => {
    switch (source) {
      case 'watchdog': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'config_sync': return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
      case 'heartbeat': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'auto_updater': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'command': return 'bg-pink-500/20 text-pink-400 border-pink-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const formatTs = (ts: number) => {
    const d = new Date(ts);
    return d.toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false, month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <Card className="bg-card border-border/50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center justify-between">
          <span
            className="flex items-center gap-2 cursor-pointer select-none"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Eye className="h-4 w-4 text-purple-400" />
            看门狗日志
            <span className="text-[10px] font-normal text-muted-foreground">(仅待机/配置同步状态)</span>
            {logsData && isExpanded && (
              <Badge variant="outline" className="text-[10px] h-4 px-1 font-normal">
                {logsData.total}条
              </Badge>
            )}
            {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </span>
          {isExpanded && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-[10px] gap-1 text-destructive"
                onClick={() => {
                  if (confirm('确定清理7天前的看门狗日志？')) cleanMutation.mutate({});
                }}
                disabled={cleanMutation.isPending}
              >
                <Trash2 className="h-3 w-3" />
                清理
              </Button>
              <Button
                variant={autoRefresh ? "default" : "outline"}
                size="sm"
                className="h-6 text-[10px] gap-1"
                onClick={() => setAutoRefresh(!autoRefresh)}
              >
                {autoRefresh ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                {autoRefresh ? '自动刷新中' : '自动刷新'}
              </Button>
              <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1" onClick={() => refetch()}>
                <RefreshCw className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`} />
                刷新
              </Button>
            </div>
          )}
        </CardTitle>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0">
          {/* 过滤工具栏 */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="relative flex-1 min-w-[150px]">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="搜索日志内容..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-7 text-xs pl-7"
              />
            </div>
            <Select value={levelFilter || 'all'} onValueChange={(v) => setLevelFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-7 w-[100px] text-xs">
                <Filter className="h-3 w-3 mr-1" />
                <SelectValue placeholder="级别" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部级别</SelectItem>
                <SelectItem value="DEBUG">DEBUG</SelectItem>
                <SelectItem value="INFO">INFO</SelectItem>
                <SelectItem value="WARNING">WARNING</SelectItem>
                <SelectItem value="ERROR">ERROR</SelectItem>
                <SelectItem value="CRITICAL">CRITICAL</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sourceFilter || 'all'} onValueChange={(v) => setSourceFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-7 w-[110px] text-xs">
                <Shield className="h-3 w-3 mr-1" />
                <SelectValue placeholder="来源" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部来源</SelectItem>
                <SelectItem value="watchdog">看门狗</SelectItem>
                <SelectItem value="config_sync">配置同步</SelectItem>
                <SelectItem value="heartbeat">心跳</SelectItem>
                <SelectItem value="auto_updater">自动更新</SelectItem>
                <SelectItem value="command">命令执行</SelectItem>
              </SelectContent>
            </Select>
            <Select value={String(hoursFilter)} onValueChange={(v) => setHoursFilter(Number(v))}>
              <SelectTrigger className="h-7 w-[90px] text-xs">
                <Clock className="h-3 w-3 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1小时</SelectItem>
                <SelectItem value="6">6小时</SelectItem>
                <SelectItem value="24">24小时</SelectItem>
                <SelectItem value="72">3天</SelectItem>
                <SelectItem value="168">7天</SelectItem>
              </SelectContent>
            </Select>
            <Select value={String(maxLines)} onValueChange={(v) => setMaxLines(Number(v))}>
              <SelectTrigger className="h-7 w-[80px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="50">50条</SelectItem>
                <SelectItem value="100">100条</SelectItem>
                <SelectItem value="200">200条</SelectItem>
                <SelectItem value="500">500条</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* 日志内容 */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">加载日志中...</span>
            </div>
          ) : !logsData?.logs || logsData.logs.length === 0 ? (
            <div className="text-center py-8">
              <Eye className="h-8 w-8 mx-auto mb-2 opacity-30 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {searchTerm || levelFilter || sourceFilter ? '没有匹配的日志记录' : '暂无看门狗日志'}
              </p>
              <div className="mt-2 space-y-1">
                <p className="text-xs text-muted-foreground">
                  看门狗日志仅在引擎处于<span className="text-yellow-400">待机</span>或<span className="text-cyan-400">配置同步</span>状态时通过心跳上报
                </p>
                <p className="text-xs text-muted-foreground">
                  引擎运行中的日志请查看「版本管理」页面的「引擎远程日志」面板
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* 统计信息 */}
              <div className="flex items-center gap-3 mb-2 text-[10px] text-muted-foreground">
                <span>显示: {logsData.logs.length} / {logsData.total}条</span>
                <span>时间范围: 最近{hoursFilter}小时</span>
                {isFetching && <Loader2 className="h-3 w-3 animate-spin" />}
              </div>

              {/* 日志终端 */}
              <div
                ref={logContainerRef}
                className="bg-black/80 rounded-lg border border-border/30 p-2 font-mono text-[11px] leading-[1.8] max-h-[500px] overflow-auto scroll-smooth"
              >
                {logsData.logs.slice().reverse().map((log: any) => (
                  <div
                    key={log.id}
                    className={`${getLevelColor(log.level)} ${getLevelBg(log.level)} hover:bg-white/5 px-2 py-0.5 rounded-sm flex items-start gap-2`}
                  >
                    <span className="text-gray-600 shrink-0 tabular-nums">{formatTs(log.timestamp)}</span>
                    <span className={`shrink-0 px-1 rounded text-[9px] font-medium border ${getLevelBadge(log.level)}`}>
                      {log.level}
                    </span>
                    <span className={`shrink-0 px-1 rounded text-[9px] border ${getSourceBadge(log.source)}`}>
                      {log.source === 'watchdog' ? '看门狗' :
                       log.source === 'config_sync' ? '配置同步' :
                       log.source === 'heartbeat' ? '心跳' :
                       log.source === 'auto_updater' ? '自动更新' :
                       log.source === 'command' ? '命令' : log.source}
                    </span>
                    <span className="break-all">{log.message}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}


// ========== 行情延迟统计面板 ==========
function QuoteLatencyPanel() {
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoursFilter, setHoursFilter] = useState(24);

  const { data: realtimeStats, isLoading: rtLoading, refetch: refetchRt } = trpc.agentMonitor.quoteLatencyStats.useQuery(
    undefined,
    { enabled: !!user && isExpanded, refetchInterval: isExpanded ? 10000 : false }
  );

  const resetMut = trpc.agentMonitor.resetQuoteLatency.useMutation({
    onSuccess: () => { refetchRt(); },
  });

  const { data: historyData, isLoading: histLoading } = trpc.agentMonitor.quoteLatencyHistory.useQuery(
    { hours: hoursFilter },
    { enabled: !!user && isExpanded, refetchInterval: isExpanded ? 30000 : false }
  );

  const levelColor = (level: string) => {
    switch (level) {
      case "good": return "text-green-400";
      case "normal": return "text-yellow-400";
      case "warning": return "text-orange-400";
      case "critical": return "text-red-400";
      case "excellent": return "text-emerald-400";
      case "fair": return "text-yellow-400";
      case "poor": return "text-red-400";
      default: return "text-muted-foreground";
    }
  };

  const levelLabel = (level: string) => {
    switch (level) {
      case "good": case "excellent": return "优秀";
      case "normal": case "fair": return "正常";
      case "warning": return "偏高";
      case "critical": case "poor": return "严重";
      default: return "未知";
    }
  };

  const levelBg = (level: string) => {
    switch (level) {
      case "good": case "excellent": return "bg-green-500/10 border-green-500/30";
      case "normal": case "fair": return "bg-yellow-500/10 border-yellow-500/30";
      case "warning": return "bg-orange-500/10 border-orange-500/30";
      case "critical": case "poor": return "bg-red-500/10 border-red-500/30";
      default: return "bg-muted/10 border-border/30";
    }
  };

  return (
    <Card className="bg-card border-border/50">
      <CardHeader className="cursor-pointer py-3 px-4" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Activity className="h-4 w-4 text-cyan-400" />
            行情延迟统计
            {historyData?.stats && (
              <Badge variant="outline" className={`text-[10px] ${levelColor(historyData.stats.count > 0 ? (historyData.stats.avg < 300 ? 'good' : historyData.stats.avg < 800 ? 'normal' : 'critical') : 'good')}`}>
                {historyData.stats.count > 0 ? `平均 ${historyData.stats.avg}ms` : '无数据'}
              </Badge>
            )}
          </CardTitle>
          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent className="px-4 pb-4 pt-0 space-y-4">
          {/* 实时行情延迟（每只股票） */}
          <div>
            <h4 className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
              <Radio className="h-3 w-3 text-green-400" />
              实时行情延迟（每只股票）
            </h4>
            {rtLoading ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />加载中...
              </div>
            ) : realtimeStats && (realtimeStats as any).perStock?.length > 0 ? (
              <div className="space-y-3">
                {/* 数据源标识 + 重置按钮 */}
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="text-[9px]">
                    数据源: {(realtimeStats as any).source === 'python' ? 'Python引擎端' : 'Web端内存'}
                  </Badge>
                  {(realtimeStats as any).source === 'python' && (
                    <Button variant="ghost" size="sm" className="h-5 text-[10px] px-2"
                      onClick={() => resetMut.mutate()}
                      disabled={resetMut.isPending}
                    >
                      {resetMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3 mr-1" />}
                      重置统计
                    </Button>
                  )}
                </div>

                {/* 全局概览（Python端数据） */}
                {(realtimeStats as any).global && (realtimeStats as any).global.sampleCount > 0 && (
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                    {[
                      { label: '平均', value: `${(realtimeStats as any).global.avg}ms`, color: 'text-cyan-400' },
                      { label: 'P50', value: `${(realtimeStats as any).global.p50}ms`, color: 'text-green-400' },
                      { label: 'P95', value: `${(realtimeStats as any).global.p95}ms`, color: 'text-yellow-400' },
                      { label: 'P99', value: `${(realtimeStats as any).global.p99}ms`, color: 'text-orange-400' },
                      { label: '样本数', value: `${(realtimeStats as any).global.sampleCount}`, color: 'text-foreground' },
                      { label: '超时', value: `${(realtimeStats as any).global.timeoutCount}`, color: (realtimeStats as any).global.timeoutCount > 0 ? 'text-red-400' : 'text-green-400' },
                    ].map(item => (
                      <div key={item.label} className="bg-muted/20 rounded-lg p-2 text-center">
                        <p className="text-[10px] text-muted-foreground">{item.label}</p>
                        <p className={`text-sm font-mono font-bold ${item.color}`}>{item.value}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* 每只股票的延迟 */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {(realtimeStats as any).perStock.map((stat: any) => {
                    const avgLevel = stat.avg < 200 ? 'good' : stat.avg < 500 ? 'normal' : stat.avg < 1000 ? 'warning' : 'critical';
                    return (
                      <div key={stat.stockCode} className={`rounded-lg border p-3 ${levelBg(avgLevel)}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs font-medium">{stat.stockName}</span>
                          <Badge variant="outline" className={`text-[9px] px-1 ${levelColor(avgLevel)}`}>
                            {levelLabel(avgLevel)}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-4 gap-1 text-[10px]">
                          <div>
                            <span className="text-muted-foreground">平均</span>
                            <p className={`font-mono font-medium ${levelColor(avgLevel)}`}>{stat.avg ?? stat.avgMs ?? 0}ms</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">P95</span>
                            <p className="font-mono font-medium text-foreground">{stat.p95 ?? stat.maxMs ?? 0}ms</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">最小</span>
                            <p className="font-mono font-medium text-foreground">{stat.min ?? stat.minMs ?? 0}ms</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">最大</span>
                            <p className="font-mono font-medium text-foreground">{stat.max ?? stat.maxMs ?? 0}ms</p>
                          </div>
                        </div>
                        <div className="mt-1 flex justify-between text-[9px] text-muted-foreground">
                          <span>样本: {stat.sampleCount}</span>
                          {stat.timeoutCount > 0 && <span className="text-red-400">超时: {stat.timeoutCount}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">暂无实时行情延迟数据（引擎启动后开始采集）</p>
            )}
          </div>

          <Separator className="opacity-30" />

          {/* 引擎连接延迟历史 */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" />
                引擎连接延迟历史
              </h4>
              <Select value={String(hoursFilter)} onValueChange={(v) => setHoursFilter(Number(v))}>
                <SelectTrigger className="h-6 w-24 text-[10px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">最近1小时</SelectItem>
                  <SelectItem value="6">最近6小时</SelectItem>
                  <SelectItem value="24">最近24小时</SelectItem>
                  <SelectItem value="72">最近3天</SelectItem>
                  <SelectItem value="168">最近7天</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {histLoading ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />加载中...
              </div>
            ) : historyData ? (
              <div className="space-y-3">
                {/* 统计概览 */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  <div className="bg-muted/20 rounded-lg p-2 text-center">
                    <p className="text-[10px] text-muted-foreground">平均延迟</p>
                    <p className="text-sm font-mono font-bold text-cyan-400">{historyData.stats.avg}ms</p>
                  </div>
                  <div className="bg-muted/20 rounded-lg p-2 text-center">
                    <p className="text-[10px] text-muted-foreground">最大延迟</p>
                    <p className="text-sm font-mono font-bold text-orange-400">{historyData.stats.max}ms</p>
                  </div>
                  <div className="bg-muted/20 rounded-lg p-2 text-center">
                    <p className="text-[10px] text-muted-foreground">最小延迟</p>
                    <p className="text-sm font-mono font-bold text-green-400">{historyData.stats.min}ms</p>
                  </div>
                  <div className="bg-muted/20 rounded-lg p-2 text-center">
                    <p className="text-[10px] text-muted-foreground">采样数</p>
                    <p className="text-sm font-mono font-bold">{historyData.stats.count}</p>
                  </div>
                  <div className="bg-muted/20 rounded-lg p-2 text-center">
                    <p className="text-[10px] text-muted-foreground">断连次数</p>
                    <p className={`text-sm font-mono font-bold ${historyData.stats.disconnectCount > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {historyData.stats.disconnectCount}
                    </p>
                  </div>
                </div>

                {/* 延迟时间线 */}
                {historyData.history && historyData.history.length > 0 && (
                  <div className="bg-black/20 rounded-lg p-3 border border-border/30">
                    <p className="text-[10px] text-muted-foreground mb-2">延迟时间线（最近 {historyData.history.length} 条记录）</p>
                    <div className="flex items-end gap-[1px] h-16 overflow-hidden">
                      {historyData.history.slice(-120).map((entry: any, i: number) => {
                        const maxH = Math.max(...historyData.history.slice(-120).map((e: any) => e.latencyMs > 0 ? e.latencyMs : 0), 1);
                        const h = entry.latencyMs > 0 ? Math.max(2, (entry.latencyMs / maxH) * 60) : 60;
                        const color = entry.status !== 'connected' ? 'bg-red-500' :
                          entry.latencyMs < 300 ? 'bg-green-500' :
                          entry.latencyMs < 800 ? 'bg-yellow-500' :
                          'bg-orange-500';
                        return (
                          <div key={i} className={`${color} opacity-70 hover:opacity-100 transition-opacity`}
                            style={{ height: `${h}px`, width: '100%', minWidth: '1px', maxWidth: '4px' }}
                            title={`${formatBJ(entry.createdAt, "time")} - ${entry.latencyMs}ms (${entry.status})`}
                          />
                        );
                      })}
                    </div>
                    <div className="flex justify-between text-[8px] text-muted-foreground mt-1">
                      <span>绿色: &lt;300ms</span>
                      <span>黄色: 300-800ms</span>
                      <span>橙色: &gt;800ms</span>
                      <span>红色: 断连</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">暂无延迟历史数据</p>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}


// ========== 多引擎实例管理面板 ==========
function EngineInstancesPanel() {
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [confirmShutdown, setConfirmShutdown] = useState<string | null>(null);
  const _isTH = checkTradingHours().isTradingHours;

  const { data, isLoading, refetch, isFetching } = trpc.tradingProxy.engineInstances.useQuery(undefined, {
    enabled: !!user,
    refetchInterval: smartInterval(30000, _isTH, 120000),
  });

  const shutdownMutation = trpc.tradingProxy.shutdownEngine.useMutation({
    onSuccess: (result) => {
      toast.success(result.message);
      setConfirmShutdown(null);
      refetch();
    },
    onError: (err) => toast.error(`停止失败: ${err.message}`),
  });

  const markOfflineMutation = trpc.tradingProxy.markEngineOffline.useMutation({
    onSuccess: () => {
      toast.success("已标记为离线");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const formatTime = (ts: number) => {
    if (!ts) return "--";
    return formatBJ(ts, "datetime");
  };

  const formatElapsed = (ts: number) => {
    if (!ts) return "--";
    const diff = Date.now() - ts;
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return `${seconds}秒前`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}分钟前`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}小时${minutes % 60}分前`;
    return `${Math.floor(hours / 24)}天前`;
  };

  // 没有数据或没有实例时不显示
  if (!data || data.instances.length === 0) return null;

  const hasConflict = data.hasConflict;
  const activeInstances = data.instances.filter(i => i.isActive);

  return (
    <>
      {/* 冲突告警横幅 */}
      {hasConflict && (
        <div className="rounded-lg border-2 border-red-500/50 bg-red-500/10 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-red-400 flex-shrink-0 mt-0.5 animate-pulse" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-400">多引擎冲突警告</h3>
              <p className="text-sm text-red-300/80 mt-1">{data.conflictMessage}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                {activeInstances.map(inst => (
                  <Badge key={inst.instanceId} variant="outline" className="border-red-500/50 text-red-300 gap-1">
                    <Server className="h-3 w-3" />
                    {inst.hostName || inst.hostIp || '未知主机'} ({inst.mode})
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-red-300/60 mt-2">
                ℹ️ 同一时间只应有一个引擎实例运行，否则可能导致重复交易、日志混乱、配置冲突。请在下方列表中停止多余的引擎实例。
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 引擎实例列表 */}
      <Card className={`bg-card ${hasConflict ? 'border-red-500/30' : 'border-border/50'}`}>
        <CardHeader
          className="cursor-pointer hover:bg-accent/30 transition-colors py-3"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Server className="h-4 w-4 text-blue-400" />
              <CardTitle className="text-sm">引擎实例管理</CardTitle>
              <Badge variant={hasConflict ? "destructive" : "outline"} className="text-xs">
                {data.activeCount} 个在线
              </Badge>
              {hasConflict && (
                <Badge variant="destructive" className="text-xs animate-pulse">冲突</Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); refetch(); }}>
                <RefreshCw className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </div>
          </div>
        </CardHeader>

        {isExpanded && (
          <CardContent className="pt-0 space-y-3">
            {data.instances.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">暂无引擎实例记录</p>
            ) : (
              <div className="space-y-2">
                {data.instances.map(inst => {
                  const isActive = inst.isActive;
                  const isShutdownRequested = inst.status === 'shutdown_requested';
                  const isOffline = inst.status === 'offline';

                  return (
                    <div
                      key={inst.instanceId}
                      className={`rounded-lg border p-3 ${
                        isActive
                          ? hasConflict
                            ? 'border-red-500/30 bg-red-500/5'
                            : 'border-green-500/30 bg-green-500/5'
                          : isShutdownRequested
                            ? 'border-yellow-500/30 bg-yellow-500/5'
                            : 'border-border/30 bg-muted/20 opacity-60'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {/* 状态指示器 */}
                          <div className={`h-2.5 w-2.5 rounded-full ${
                            isActive ? 'bg-green-400 animate-pulse'
                            : isShutdownRequested ? 'bg-yellow-400'
                            : 'bg-gray-500'
                          }`} />

                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">
                                {inst.hostName || '未知主机'}
                              </span>
                              {inst.hostIp && (
                                <span className="text-xs text-muted-foreground">
                                  <Globe className="h-3 w-3 inline mr-0.5" />{inst.hostIp}
                                </span>
                              )}
                              <Badge variant="outline" className="text-xs">
                                {inst.mode === 'live' ? '实盘' : '模拟'}
                              </Badge>
                              {inst.engineVersion && (
                                <Badge variant="secondary" className="text-xs">
                                  v{inst.engineVersion}
                                </Badge>
                              )}
                              {isActive && (
                                <Badge variant="default" className="text-xs bg-green-600">在线</Badge>
                              )}
                              {isShutdownRequested && (
                                <Badge variant="default" className="text-xs bg-yellow-600">停止中...</Badge>
                              )}
                              {isOffline && (
                                <Badge variant="secondary" className="text-xs">离线</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                              <span>启动: {formatTime(inst.startedAt)}</span>
                              <span>心跳: {formatElapsed(inst.lastHeartbeat)}</span>
                              <span className="font-mono text-[10px] opacity-50">
                                ID: {inst.instanceId.substring(0, 8)}...
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* 操作按钮 */}
                        <div className="flex items-center gap-2">
                          {isActive && (
                            confirmShutdown === inst.instanceId ? (
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-red-400 mr-1">确认停止?</span>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  className="h-7 text-xs"
                                  onClick={() => shutdownMutation.mutate({ instanceId: inst.instanceId })}
                                  disabled={shutdownMutation.isPending}
                                >
                                  {shutdownMutation.isPending ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <>确认</>
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-xs"
                                  onClick={() => setConfirmShutdown(null)}
                                >
                                  取消
                                </Button>
                              </div>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs text-red-400 border-red-500/30 hover:bg-red-500/10"
                                onClick={() => setConfirmShutdown(inst.instanceId)}
                              >
                                <PowerOff className="h-3 w-3 mr-1" />远程停止
                              </Button>
                            )
                          )}
                          {!isActive && !isShutdownRequested && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => markOfflineMutation.mutate({ instanceId: inst.instanceId })}
                              disabled={isOffline}
                            >
                              清理
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {hasConflict && (
              <div className="rounded-md bg-amber-500/10 border border-amber-500/30 p-3 text-xs text-amber-300/80">
                <p className="font-medium text-amber-300 mb-1">如何解决冲突：</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>点击上方“远程停止”按钮停止多余的引擎实例</li>
                  <li>引擎将在下次心跳时收到停止命令并自动关闭（最多等待60秒）</li>
                  <li>如果远程停止无效，请手动SSH登录服务器停止引擎进程</li>
                </ol>
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </>
  );
}

// ========== 买卖配对检查面板 ==========
function TradePairCheckPanel() {
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: pairData, isLoading, refetch, isFetching } = trpc.tradePairCheck.check.useQuery(
    undefined,
    {
      enabled: !!user && isExpanded,
      refetchInterval: isExpanded ? 30000 : false,
      staleTime: 10000,
    }
  );

  // 统计概览
  const alertCount = pairData?.filter(d => d.status === "alert").length || 0;
  const warningCount = pairData?.filter(d => d.status === "warning").length || 0;
  const totalUnpaired = pairData?.reduce((s, d) => s + d.unpairedSells.length + d.unpairedBuys.length, 0) || 0;
  const totalPaired = pairData?.reduce((s, d) => s + d.pairedRounds, 0) || 0;
  const hasIssues = alertCount > 0 || warningCount > 0;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "alert": return <AlertTriangle className="h-3.5 w-3.5 text-red-400" />;
      case "warning": return <AlertCircle className="h-3.5 w-3.5 text-yellow-400" />;
      default: return <CheckCircle2 className="h-3.5 w-3.5 text-green-400" />;
    }
  };

  const getStatusBg = (status: string) => {
    switch (status) {
      case "alert": return "bg-red-500/5 border-red-500/20";
      case "warning": return "bg-yellow-500/5 border-yellow-500/20";
      default: return "";
    }
  };

  return (
    <Card className="bg-card border-border/50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center justify-between">
          <span
            className="flex items-center gap-2 cursor-pointer select-none"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Shield className="h-4 w-4 text-cyan-400" />
            买卖配对检查
            {pairData && isExpanded && (
              <>
                {hasIssues ? (
                  <Badge variant="destructive" className="text-[10px] h-4 px-1.5 font-normal">
                    {alertCount > 0 ? `${alertCount}异常` : ''}{alertCount > 0 && warningCount > 0 ? ' ' : ''}{warningCount > 0 ? `${warningCount}警告` : ''}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] h-4 px-1.5 font-normal text-green-400 border-green-500/30">
                    全部正常
                  </Badge>
                )}
              </>
            )}
            {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </span>
          {isExpanded && (
            <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1" onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              刷新
            </Button>
          )}
        </CardTitle>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0">
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : !pairData || pairData.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <Shield className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">暂无监控项数据</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* 统计概览 */}
              <div className="grid grid-cols-4 gap-2">
                <div className="p-2 rounded-lg bg-muted/30 text-center">
                  <div className="text-[10px] text-muted-foreground">监控项</div>
                  <div className="text-lg font-bold">{pairData.length}</div>
                </div>
                <div className="p-2 rounded-lg bg-muted/30 text-center">
                  <div className="text-[10px] text-muted-foreground">已配对轮次</div>
                  <div className="text-lg font-bold text-green-400">{totalPaired}</div>
                </div>
                <div className="p-2 rounded-lg bg-muted/30 text-center">
                  <div className="text-[10px] text-muted-foreground">未配对笔数</div>
                  <div className={`text-lg font-bold ${totalUnpaired > 0 ? 'text-yellow-400' : 'text-green-400'}`}>{totalUnpaired}</div>
                </div>
                <div className="p-2 rounded-lg bg-muted/30 text-center">
                  <div className="text-[10px] text-muted-foreground">异常/警告</div>
                  <div className={`text-lg font-bold ${alertCount > 0 ? 'text-red-400' : warningCount > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
                    {alertCount + warningCount}
                  </div>
                </div>
              </div>

              {/* 各监控项详情 */}
              {pairData.map((item) => (
                <div key={item.monitorId} className={`rounded-lg border p-3 ${getStatusBg(item.status)}`}>
                  {/* 标题行 */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(item.status)}
                      <span className="font-medium text-sm">{item.stockName}</span>
                      <span className="text-xs font-mono text-muted-foreground">{item.stockCode}</span>
                      <Badge variant="outline" className="text-[10px] h-4 px-1">
                        {item.engineStatus === 'idle' ? '空闲' : item.engineStatus === 'detecting' ? '检测中' : item.engineStatus === 'observing' ? '观察中' : item.engineStatus === 'waiting_fill' ? '等待配对' : item.engineStatus}
                      </Badge>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      持仓: <span className="font-mono font-medium text-foreground">{item.currentHolding}</span>股
                    </span>
                  </div>

                  {/* 交易统计 */}
                  <div className="grid grid-cols-5 gap-2 mb-2 text-xs">
                    <div className="p-1.5 rounded bg-muted/30 text-center">
                      <div className="text-[10px] text-muted-foreground">卖出</div>
                      <div className="font-medium">{item.todaySellCount}笔 <span className="text-muted-foreground">{item.todaySellShares}股</span></div>
                    </div>
                    <div className="p-1.5 rounded bg-muted/30 text-center">
                      <div className="text-[10px] text-muted-foreground">买入</div>
                      <div className="font-medium">{item.todayBuyCount}笔 <span className="text-muted-foreground">{item.todayBuyShares}股</span></div>
                    </div>
                    <div className="p-1.5 rounded bg-muted/30 text-center">
                      <div className="text-[10px] text-muted-foreground">已配对</div>
                      <div className="font-medium text-green-400">{item.pairedRounds}轮</div>
                    </div>
                    <div className="p-1.5 rounded bg-muted/30 text-center">
                      <div className="text-[10px] text-muted-foreground">净变化</div>
                      <div className={`font-medium ${item.todayBuyShares - item.todaySellShares > 0 ? 'text-red-400' : item.todayBuyShares - item.todaySellShares < 0 ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                        {item.todayBuyShares - item.todaySellShares > 0 ? '+' : ''}{item.todayBuyShares - item.todaySellShares}股
                      </div>
                    </div>
                    <div className="p-1.5 rounded bg-muted/30 text-center">
                      <div className="text-[10px] text-muted-foreground">今日轮次</div>
                      <div className="font-medium">{item.todayRounds}</div>
                    </div>
                  </div>

                  {/* 状态消息 */}
                  <div className={`text-xs px-2 py-1 rounded ${
                    item.status === 'alert' ? 'bg-red-500/10 text-red-400' :
                    item.status === 'warning' ? 'bg-yellow-500/10 text-yellow-400' :
                    'bg-green-500/10 text-green-400'
                  }`}>
                    {item.statusMessage}
                  </div>

                  {/* 未配对交易详情 */}
                  {(item.unpairedSells.length > 0 || item.unpairedBuys.length > 0) && (
                    <div className="mt-2 space-y-1">
                      <div className="text-[10px] text-muted-foreground font-medium">未配对交易明细：</div>
                      {item.unpairedSells.map((t) => (
                        <div key={t.id} className="flex items-center gap-2 text-xs px-2 py-1 rounded bg-emerald-500/5 border border-emerald-500/10">
                          <Badge className="text-[9px] h-3.5 px-1 bg-emerald-500/20 text-emerald-400 border-emerald-500/30">卖出</Badge>
                          <span className="font-mono">{t.quantity}股</span>
                          <span className="font-mono">@{t.price.toFixed(2)}</span>
                          {t.roundId != null && <span className="text-muted-foreground">R{t.roundId}</span>}
                          {t.scenarioType && <span className="text-muted-foreground">{t.scenarioType === 's1' ? '↓回补' : '↑回卖'}</span>}
                          {t.tradeReason && <span className="text-muted-foreground">({t.tradeReason})</span>}
                          <span className={`ml-auto ${t.elapsedMinutes > 30 ? 'text-red-400 font-medium' : t.elapsedMinutes > 10 ? 'text-yellow-400' : 'text-muted-foreground'}`}>
                            {t.elapsedMinutes}分钟前
                          </span>
                        </div>
                      ))}
                      {item.unpairedBuys.map((t) => (
                        <div key={t.id} className="flex items-center gap-2 text-xs px-2 py-1 rounded bg-red-500/5 border border-red-500/10">
                          <Badge className="text-[9px] h-3.5 px-1 bg-red-500/20 text-red-400 border-red-500/30">买入</Badge>
                          <span className="font-mono">{t.quantity}股</span>
                          <span className="font-mono">@{t.price.toFixed(2)}</span>
                          {t.roundId != null && <span className="text-muted-foreground">R{t.roundId}</span>}
                          {t.scenarioType && <span className="text-muted-foreground">{t.scenarioType === 's1' ? '↓回补' : '↑回卖'}</span>}
                          {t.tradeReason && <span className="text-muted-foreground">({t.tradeReason})</span>}
                          <span className={`ml-auto ${t.elapsedMinutes > 30 ? 'text-red-400 font-medium' : t.elapsedMinutes > 10 ? 'text-yellow-400' : 'text-muted-foreground'}`}>
                            {t.elapsedMinutes}分钟前
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
